import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type CSSProperties,
} from 'react';
import { useStableAnimation } from '../../hooks/useStableAnimation';
import { type AnimatedWrapperProps } from './AnimatedWrapper.types';

interface KeyframeStyles {
  [property: string]: string | number;
}

/**
 * Keyframes map, e.g.
 * {
 *   "0%": { opacity: 0 },
 *   "100%": { opacity: 1 }
 * }
 */
export interface Keyframes {
  [percent: string]: KeyframeStyles;
}

/**
 * AnimatedWrapper
 *
 * A low-level, one-shot animation helper using the Web Animations API.
 * - Runs a single keyframe animation when `isAnimationStarted` is true.
 * - Applies initial styles from the first keyframe.
 * - Applies final styles from the last keyframe when finished.
 * - Respects `prefers-reduced-motion`.
 *
 * This is intentionally *not* a presence / enter/exit API. For open/close
 * component transitions, use `TransitionWrapper`.
 */
export const AnimatedWrapper = ({
  children,
  className = '',
  style,
  animation: animationProp,
  duration = '0.3s',
  easing = 'cubic-bezier(0.4, 0, 0.2, 1)',
  isAnimationStarted = true,
  onEnd,
}: AnimatedWrapperProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const animationRef = useRef<Animation | null>(null);
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  const animation = useStableAnimation(animationProp);

  // SSR-safe prefers-reduced-motion check
  const isPrefersReducedMotion = useMemo(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const getInitialStyles = useCallback((): CSSProperties => {
    if (!animation) {
      return {};
    }
    const firstKeyframe = Object.entries(animation).sort(
      ([a], [b]) => Number.parseFloat(a) - Number.parseFloat(b)
    )[0];
    return firstKeyframe ? { ...firstKeyframe[1] } : {};
  }, [animation]);

  const getFinalStyles = useCallback((): CSSProperties => {
    if (!animation) {
      return {};
    }
    const lastKeyframe = Object.entries(animation)
      .sort(([a], [b]) => Number.parseFloat(a) - Number.parseFloat(b))
      .slice(-1)[0];
    return lastKeyframe ? { ...lastKeyframe[1] } : {};
  }, [animation]);

  // Compute initial styles once per animation change.
  // When the animation hasn't started yet we strip `transform` so that
  // off-screen translations (e.g. slideInRight's translateX(100%)) don't
  // cause a horizontal scrollbar while the element is still invisible.
  const initialStyles = useMemo(() => {
    const base = getInitialStyles();
    if (!isAnimationStarted) {
      const { transform, ...rest } = base as CSSProperties & {
        transform?: string;
      };
      return { ...rest, ...style };
    }
    return { ...base, ...style };
  }, [getInitialStyles, style, isAnimationStarted]);

  useEffect(() => {
    const node = ref.current;

    // Early exit: no animation possible
    if (!node || !animation || !isAnimationStarted) {
      return undefined;
    }

    // Cancel any previous animation if the effect reruns
    if (animationRef.current) {
      animationRef.current.cancel();
      animationRef.current = null;
    }

    // Reduced motion: skip animation and apply final styles immediately
    if (isPrefersReducedMotion) {
      const finalStyles = getFinalStyles();
      Object.assign(node.style, finalStyles);
      onEndRef.current?.();
      return undefined;
    }

    // Prepare Web Animations API frames
    const keyframes: (Keyframe & KeyframeStyles)[] = Object.entries(animation)
      .sort(([a], [b]) => Number.parseFloat(a) - Number.parseFloat(b))
      .map(([percent, styles]) => ({
        offset: Number.parseFloat(percent) / 100,
        ...styles,
      }));

    // Normalize duration to ms
    const durationMs = duration.endsWith('ms')
      ? Number.parseFloat(duration)
      : Number.parseFloat(duration) * 1000;

    // Start animation
    const instance = node.animate(keyframes, {
      duration: durationMs,
      easing,
      fill: 'forwards',
    });

    animationRef.current = instance;

    instance.onfinish = () => {
      const rawFinalStyles = keyframes[keyframes.length - 1] as KeyframeStyles;
      // When the final frame hides the element (opacity 0), strip `transform`
      // so that off-screen translations (e.g. slideOutRight) don't leave the
      // element positioned in a way that causes a horizontal scrollbar.
      const finalStyles: KeyframeStyles =
        rawFinalStyles.opacity === 0
          ? Object.fromEntries(
              Object.entries(rawFinalStyles).filter(([k]) => k !== 'transform')
            )
          : rawFinalStyles;
      // Cancel the animation before applying inline styles. fill:'forwards'
      // keeps the animation layer active (including any transform), which
      // overrides inline styles. Cancelling removes that layer so our
      // filtered finalStyles take full effect.
      instance.cancel();
      Object.assign(node.style, finalStyles);
      animationRef.current = null;
      onEndRef.current?.();
    };

    // Cleanup always returned for consistent-return
    return () => {
      if (animationRef.current) {
        animationRef.current.cancel();
        animationRef.current = null;
      }
    };
  }, [
    animation,
    duration,
    easing,
    isAnimationStarted,
    isPrefersReducedMotion,
    getFinalStyles,
  ]);

  return (
    <div ref={ref} className={className} style={initialStyles}>
      {children}
    </div>
  );
};
